<template>
    <input
        :name="name"
        :class="classes"
        @input="handleInput"
        autocomplete="off"
        :style="cellStyle"
        type="text"
        :data-row="dataoptions ? dataoptions['data-row'] : null"
        :data-col="dataoptions ? dataoptions['data-col'] : null"
        :data-fieldtype="dataoptions ? dataoptions['data-fieldtype'] : null"
        :data-fieldname="dataoptions ? dataoptions['data-fieldname'] : null"
        :ref="reference || name"
        :required="required"
        :disabled="disabled"
        :readonly="disabled"
        :placeholder="placeholder"
        :tabindex="tabindex"
        v-model="date"
        @focus="handleFocus"
        @blur="handleBlur"
    />
</template>

<script>
import {defineComponent} from 'vue';
import {DateTime} from 'luxon';
export default defineComponent ({
    emits: ['update:modelValue', 'focus', 'change', 'blur', 'verify-date'],
    name: "DateField",
    props: {
        modelValue: {
            type: [String, Number],
            required: false,
            default: "",
        },
        name: {
            type: String,
            required: false,
            default: "",
        },
        required: {
            type: Boolean,
            required: false,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        placeholder: {
            type: String,
            required: false,
            default: () => this.locale === 'en-US' ? 'mm-dd-yyyy' : 'yyyy-mm-dd',
        },
        tabindex: {
            type: String,
            required: false,
            default: "",
        },
        reference: {
            type: String,
            required: false,
            default: "",
        },
        dataoptions: {
            type: Object,
            required: false,
        },
        format: {
            type: String,
            required: false,
            default: 'MM/dd/yyyy',
        },
        allowableRange: {
            type: Object,
            required: false,
            validator: (value) => {
                return (
                    typeof value.start === "number" &&
                    typeof value.end === "number" &&
                    value.start < value.end
                );
            },
        },
        disabledDaysOfWeek: {
            type: Array,
            default() {
                return []
            }
        },
        classes: {
            type: [ String, Array ],
            required: false,
            default: 'form-control'
        },
        allowSetNa: {
            type: Boolean,
            default: false
        },
        allowEmpty: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            date: "",
            locale: window.navigator.userLanguage || window.navigator.language || "en-US", // 'fr-fr' // test stuff
        };
    },
    methods: {
        // to check valid date or not. ex) 2/30/2030 -> invalid.
        isValidDate(month, day, year) {
            month = month - 1;
            var d = new Date(year, month, day);
            if (d.getFullYear() == year && d.getMonth() == month && d.getDate() == day) {
              return true;
            }
            return false;
        },
        determineLocale() {
            if (!window.navigator || !window.navigator.languages) return 'en-us';
            const userLocale = ( window.navigator.languages && window.navigator.languages.length ? window.navigator.languages[0] : window.navigator.language) || 'en-us'
            return userLocale.toLowerCase();
        },
        /**
        * Handles focus event
        * @param e   FocusEvent
        */
        handleFocus(e) {
            this.$emit("focus", e);
        },
        /**
        * Handles user input. Fires an input event to update v-model, as well as a change event since the input
        * value as changed.
        */
        handleInput() {
            this.$emit("update:modelValue", this.date);
            this.$emit("change");
        },
        /**
        * Handles blur event. Emits a blur event (standard), as well as an input event for v-model update after date is
        * formatted to the correct format. Also fires a change event since the value is changed after it is formatted.
        * @param e   FocusEvent
        */
        handleBlur(e) {
            if (e.target.value) {
                this.date = this.formatSingleDateString(e.target.value);
            }
            this.$emit("blur", e);
            this.$emit("update:modelValue", this.formatSingleDateString(e.target.value, true));
            this.$emit("change");
            // simple target value change to YYYY-MM-DD to use target parameter in verifyDate function.
            e.target.value = this.date;
            this.$emit("verify-date", e.target, 'expiration');
        },
        /**
        * Displays an error notification to the user
        * @param msg   The error message
        */
        errHandler(msg) {
            return this.$message({
                showClose: false,
                message: msg || "Invalid date format",
                type: "error",
            });
        },
        /**
        * Parses user-provided date string into Object containing day, month, year,
        * then uses moment.js to create date object and format it into YYYY-MM-DD
        * @return {string}
        */
        formatSingleDateString(dateString) {
            if (this.allowSetNa && dateString.toLowerCase() == 'na') return 'NA'
            if (!(dateString && dateString.length)) return;

            let dateObject = this.parseUserDateFormats(dateString);
            // console.log(dateObject, dateObject.toISOString().split('T')[0]);
            const date = dateObject ? dateObject.toISOString().split('T')[0] : null;
            this.date = date;
            return date;
        },
        /**
        * Checks whether the user-defined date falls within the range passed via the "allowableRange" prop's "range" property.
        * @param momentDateObject  The momentjs object representation of the user-defined date
        */
        // dateIsWithinAllowableRange(momentDateObject) {
        //     try {
        //         const { start, end } = this.allowableRange;
        //         let dateAtRangeStart = moment(new Date())
        //         .add(start, "days")
        //         .format("YYYY-MM-DD");
        //         let dateAtRangeEnd = moment(new Date())
        //         .add(end + 1, "days")
        //         .format("YYYY-MM-DD");
        //         return momentDateObject.isBetween(dateAtRangeStart, dateAtRangeEnd);
        //     } catch (e) {
        //         console.log(e);
        //         return true;
        //     }
        // },
        /**
        * Parses user provided date string into Object containing day, month, and year
        * @return {object}
        */
        parseUserDateFormats(dateString) {
            if (!dateString || (typeof dateString !== 'string' && typeof dateString !== 'number')) {
                // console.log('Invalid characters');
                return null;
            }
            // const locale = this.determineLocale();
            let date = String(dateString).replace(/[ \.\,\/-]/g, '/').split('/');

            if (!date || date.length < 1) {
                // console.log('Cannot Parse date');
                return null;
            }
            for (const i in date) {
                if (typeof date[i] === 'function') continue;
                if (isNaN(date[i])) {
                    // console.log('Invalid characters ' + date[i]);
                    return null;
                }
            }

            let result = { day: null, month: null, year: null };
            let flagToChangeMonthDay = false;

            if (date.length > 3) {
                // console.log('Cannot Parse date.  Too many puncuation marks');
                // console.log(date.length, date);
                return null;

            // If here, past basic validations
            // YYYYMMDD || MMDDYYYY or inverse
            } else if (date.length === 1) {
                // It's not common and our system doesn't expect one line for date field in all other section. to make this consistant. but If there is a issue or need to change it, will work on that.
                // result = this.handleUndelimitedDateString(date[0]);
                return null;

            // if 3 elements, must be month, day, and year
            // Figure out the year and then assume the last 2 elements are the month and day
            } else if (date.length === 3) {
                // Figure out if 1st or 3rd element is year
                if (String(date[2]).length === 4) {
                    result.year = date[2];
                    date.splice(2, 1);
                } else if (String(date[0]).length === 4) {
                    result.year = date[0];
                    flagToChangeMonthDay = true;
                    date.splice(0, 1);
                } else if (String(date[2]).length === 2 && date[2] > 31) {
                    result.year = date[2];
                    date.splice(2, 1);
                } else if (String(date[0]).length === 2 && date[0] > 31) {
                    result.year = date[0];
                    flagToChangeMonthDay = true;
                    date.splice(0, 1);
                } else {
                    // Assume it's the 3rd element if the above is not conclusive since most of users are in the US
                    result.year = date[2];
                    date.splice(2, 1);
                }
            }

            if (date.length !== 2) {
                // console.log('Cannot Parse date.  Too many puncuation marks');
                // console.log(date.length, date);
                return null;
            }

            // Assuming if here, trying to figure out the month and date.
            result.year = result.year || new Date().getFullYear();

            // Make a 2 digit year a 4-digit one by guessing...
            if (result.year <= 35) { result.year = parseInt('20' + result.year)}
            else if (result.year <= 99) { result.year = parseInt('19' + result.year)}

            // console.log('Date', result);
            if (this.format == 'MM/dd/yyyy' || this.format == 'yyyy/MM/dd') {
                result.month = date[0];
                result.day = date[1];
            } else {
                if (flagToChangeMonthDay) {
                    // to make support for dd/mm/yyyy date format when user type yyyy/mm/dd.
                    result.day = date[1];
                    result.month = date[0];
                } else {
                    result.day = date[0];
                    result.month = date[1];
                }
            }

            // Let's see if we guessed right
            let finalDate = new Date(`${result.year}-${result.month}-${result.day}`);
            // console.log('Final Date', finalDate);
            if (finalDate instanceof Date && !isNaN(finalDate) && this.isValidDate(result.month,result.day,result.year)) return finalDate;

            // Maybe non-US format
            finalDate = new Date(`${result.year}-${result.day}-${result.month}`);
            // console.log('Final Date', finalDate);
            if (finalDate instanceof Date && !isNaN(finalDate) && this.isValidDate(result.month,result.day,result.year)) return finalDate;

            return null;
        },
        /**
        * Parses user-provided date string when string contains no delimiters (i.e. '/' or '-')
        * Arranges the date string in two possible ways, the first being one where the year
        * might be at the start (yearIsAtStart) and the second being one where the year might
        * be at the end (yearIsAtEnd), then compare yearIsAtStart and yearIsAtEnd to determine
        * which is more likely to be a valid date. If nothing matches, returns null.
        * @param date
        * @return {*}
        */
        handleUndelimitedDateString(date) {
            if (!(date.length === 8 || date.length === 6)) return null;
            let yearIsAtStart, yearIsAtEnd, firstTwoDigits, lastTwoDigits;
            if (date.length === 8) {
                yearIsAtStart = [
                // YYYYMMDD
                date.substring(0, 4),
                date.substring(4, 6),
                date.substring(6, 8),
                ];
                yearIsAtEnd = [
                // MMDDYYYY
                date.substring(0, 2),
                date.substring(2, 4),
                date.substring(4, 8),
                ];
                firstTwoDigits = parseInt(yearIsAtStart[0][0] + yearIsAtStart[0][1]);
                if (firstTwoDigits > 12 && [19, 20, 21].includes(firstTwoDigits)) {
                return {
                    day: yearIsAtStart[2],
                    month: yearIsAtStart[1],
                    year: yearIsAtStart[0],
                };
                }
                if (firstTwoDigits <= 12) {
                return {
                    day: yearIsAtEnd[1],
                    month: yearIsAtEnd[0],
                    year: yearIsAtEnd[2],
                };
                }
            } else if (date.length === 6) {
                yearIsAtEnd = [
                // MMDDYY
                date.substring(0, 2),
                date.substring(2, 4),
                date.substring(4, 6),
                ];
                firstTwoDigits = parseInt(yearIsAtEnd[0][0] + yearIsAtEnd[0][1]);
                lastTwoDigits = parseInt(yearIsAtEnd[2][0] + yearIsAtEnd[2][1]);
                const getCentury = (twoDigitYear) => {
                return twoDigitYear >= 50 && twoDigitYear <= 99 ? 1900 : 2000;
                };
                if (firstTwoDigits <= 12) {
                return {
                    day: yearIsAtEnd[1],
                    month: yearIsAtEnd[0],
                    year: getCentury(lastTwoDigits) + lastTwoDigits,
                };
                }
            }
            return null;
        },
        /**
        * Parses a two digit year, returning 20xx if year < 50, or 19xx if year > 50
        * @param year
        */
        parseTwoDigitYear(year) {
            return year >= 50 ? parseInt(`19${year}`) : parseInt(`20${year}`);
        },
    },
    computed: {
        /**
        * Returns a boolean indicating whether the provided range in this.allowableRange is valid. Returns true if the
        * range start and end are both numbers and the value of start is less than the value of end.
        */
        allowableRangeIsValid() {
            const range = this.allowableRange;
            return (
                range &&
                typeof range.start === "number" &&
                typeof range.end === "number" &&
                range.start < range.end
            );
        },
        /**
        * Dynamically sets the style of this field depending on certain conditions such as whether it is disabled
        */
        cellStyle() {
            let style = {};
            if (this.disabled) {
                style.cursor = "not-allowed";
                style["background-color"] = "#eeeeee";
            } else if (this.required) {
                style["background-color"] = "#e2f0f5";
            } else {
                delete style.cursor;
                delete style.background;
            }
            //return { ...style, ...this.customStyle };
            return { ...style };
        },
        // True if there is an invalid date
        isError() {
            if (!this.date) return false;
            if (this.allowSetNa && this.date === 'NA') return false;

            let finalDate;
            try {
                finalDate = new Date(this.date);
            } catch (error) {
                // console.log(error);
            }

            if (!finalDate) return true;
            const month = parseInt(finalDate.getMonth()) + 1;
            const day = finalDate.getDate();
            const year = finalDate.getFullYear();
            const test = this.isValidDate(month, day, year) ? false : true;
            return test;
        }
    },
    watch: {
        modelValue() {
            if (this.modelValue) this.date = this.modelValue;
            else if (this.allowEmpty && (this.modelValue === '' || this.modelValue === null)) this.date = this.modelValue;
        },
    },
    created() {
        if (this.modelValue) this.date = this.formatSingleDateString(DateTime.fromFormat(this.modelValue,'MM/dd/yyyy').toFormat(this.format));
    },
});
</script>

<style scoped>
.date-field {
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #bfcbd9;
    box-sizing: border-box;
    color: #1f2d3d;
    font-size: inherit;
    height: 36px;
    line-height: 1;
    outline: 0;
    padding: 3px 10px;
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;
}
</style>

